import React from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import { formatNumber } from '~/utils/helpers'
import { WidgetCard, WidgetTitle } from '../../style'
import { MilchmengeContainer } from './style'
import { useMilkMoney } from '../../MilkMoneyContext'
import { MilkAmountWidgetSkeleton } from '../../Skeletons'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

const MilchmengeWidget: React.FC = () => {
  const { t } = useTranslation()
  const { milkMoneyData, isLoading } = useMilkMoney()

  if (isLoading) return <MilkAmountWidgetSkeleton />

  const amounts = milkMoneyData?.amounts ?? []

  if (!amounts.length) {
    return (
      <WidgetCard>
        <WidgetTitle>{t('Milk amount')}</WidgetTitle>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#999', fontSize: '14px' }}>
          {t('No data available')}
        </div>
      </WidgetCard>
    )
  }

  const chartData = amounts.map((a) => ({
    day: dayjs(a.date).date(),
    amount: parseFloat(a.amount)
  }))

  const maxAmount = Math.ceil(Math.max(...chartData.map((d) => d.amount)) * 1.2 / 1000) * 1000

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '10px'
        }}>
          <p style={{ margin: 0 }}>{`Tag ${label}`}</p>
          <p style={{ margin: 0, color: '#03A9F4' }}>
            {`Menge: ${formatNumber(payload[0].value, 0)} kg`}
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <WidgetCard>
      <WidgetTitle>{t('Milk amount')}</WidgetTitle>
      <MilchmengeContainer>
        <ResponsiveContainer width='100%' height='100%'>
          <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray='3 3' vertical={false} />
            <XAxis
              dataKey='day'
              tickFormatter={(value) => value.toString().padStart(2, '0')}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={(value) => `${value / 1000}K`}
              axisLine={false}
              tickLine={false}
              domain={[0, maxAmount]}
            />
            <Tooltip content={<CustomTooltip />} />
            <defs>
              <linearGradient id='colorAmount' x1='0' y1='0' x2='0' y2='1'>
                <stop offset='5%' stopColor='#03A9F4' stopOpacity={0.8} />
                <stop offset='95%' stopColor='#03A9F4' stopOpacity={0.3} />
              </linearGradient>
            </defs>
            <Area
              type='monotone'
              dataKey='amount'
              stroke='#03A9F4'
              fill='url(#colorAmount)'
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </MilchmengeContainer>
    </WidgetCard>
  )
}

export default MilchmengeWidget
